import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import { Rnd } from 'react-rnd';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import CallEndIcon from '@mui/icons-material/CallEnd';
import MicIcon from '@mui/icons-material/Mic';
import MicOffIcon from '@mui/icons-material/MicOff';
import VideocamIcon from '@mui/icons-material/Videocam';
import VideocamOffIcon from '@mui/icons-material/VideocamOff';
import ChatIcon from '@mui/icons-material/Chat';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { useNavigate } from 'react-router-dom';
import JitsiCall from './JitsiCall';
import { useCall } from '../../contexts/CallContext';
import { useChat } from '../../contexts/ChatContext';
import useCallLayout from '../../hooks/useCallLayout';
import config from '../../config';
import api from '../../services/api';

// ─── CallInfoBar ────────────────────────────────────────────────
// Top overlay: participant name, elapsed timer, and live billing info
const CallInfoBar = () => {
  const { activeCall, updateBilling } = useCall();
  const [elapsed, setElapsed] = useState('00:00');

  useEffect(() => {
    if (!activeCall?.callStartTime) return;
    const tick = () => {
      const secs = Math.floor((Date.now() - activeCall.callStartTime) / 1000);
      const m = String(Math.floor(secs / 60)).padStart(2, '0');
      const s = String(secs % 60).padStart(2, '0');
      setElapsed(`${m}:${s}`);
      // Update totalCharged from elapsed time x rate (client-side computation)
      const rate = activeCall.ratePerMin || 0;
      if (rate > 0) {
        updateBilling({ totalCharged: (secs / 60) * rate });
      }
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [activeCall?.callStartTime, activeCall?.ratePerMin, updateBilling]);

  const rate = activeCall?.ratePerMin || 0;
  const totalCharged = activeCall?.totalCharged || 0;
  const astrologerEarning = activeCall?.astrologerEarning || 0;
  const hasBilling = rate > 0;

  return (
    <Box sx={{
      position: 'absolute', top: 0, left: 0, right: 0, zIndex: 11,
      display: 'flex', flexDirection: 'column',
      bgcolor: 'rgba(0,0,0,0.5)', pointerEvents: 'none',
    }}>
      {/* Row 1: participant name + elapsed timer */}
      <Box sx={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        px: 1.5, py: 0.5,
      }}>
        <Typography variant="caption" sx={{ color: '#fff', fontWeight: 500, fontSize: '0.75rem' }}>
          {activeCall?.participantName || 'Connecting...'}
        </Typography>
        <Typography variant="caption" sx={{
          color: '#fff', fontFamily: 'monospace', fontSize: '0.75rem',
          fontVariantNumeric: 'tabular-nums',
        }}>
          {elapsed}
        </Typography>
      </Box>
      {/* Row 2: billing info (rate, charge, earning) — only when rate is known */}
      {hasBilling && (
        <Box
          data-testid="billing-overlay"
          sx={{
            display: 'flex', justifyContent: 'center', alignItems: 'center',
            gap: 1.5, px: 1, pb: 0.5,
          }}
        >
          <Typography variant="caption" sx={{ color: '#ffd54f', fontSize: '0.65rem' }}>
            {'₹'}{rate}/min
          </Typography>
          <Typography variant="caption" data-testid="user-charge" sx={{ color: '#ef9a9a', fontSize: '0.65rem' }}>
            Spent: {'₹'}{Math.round(totalCharged)}
          </Typography>
          {astrologerEarning > 0 && (
            <Typography variant="caption" data-testid="astrologer-earning" sx={{ color: '#a5d6a7', fontSize: '0.65rem' }}>
              Earned: {'₹'}{Math.round(astrologerEarning)}
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
};

// ─── CallToolbar ────────────────────────────────────────────────
const CallToolbar = ({ compact = false, onEnd, onChatClick, onUserDetailsClick, participantName }) => {
  const { activeCall, toggleAudio, toggleVideo } = useCall();
  const iconSize = compact ? 18 : 24;
  const btnSize = compact ? 32 : 44;
  const endBtnSize = compact ? 36 : 52;

  return (
    <Box sx={{
      display: 'flex', justifyContent: 'center', alignItems: 'center',
      gap: compact ? 0.5 : 2, py: compact ? 0.25 : 1, px: 1,
      bgcolor: 'rgba(0,0,0,0.7)', borderRadius: compact ? 2 : 0,
    }}>
      <Tooltip title={activeCall?.isAudioMuted ? 'Unmute' : 'Mute'}>
        <IconButton onClick={toggleAudio} sx={{
          color: activeCall?.isAudioMuted ? '#f44336' : '#fff',
          bgcolor: activeCall?.isAudioMuted ? 'rgba(244,67,54,0.2)' : 'rgba(255,255,255,0.1)',
          width: btnSize, height: btnSize,
          '&:hover': { bgcolor: activeCall?.isAudioMuted ? 'rgba(244,67,54,0.3)' : 'rgba(255,255,255,0.2)' },
        }}>
          {activeCall?.isAudioMuted ? <MicOffIcon sx={{ fontSize: iconSize }} /> : <MicIcon sx={{ fontSize: iconSize }} />}
        </IconButton>
      </Tooltip>
      <Tooltip title={activeCall?.isVideoMuted ? 'Camera on' : 'Camera off'}>
        <IconButton onClick={toggleVideo} sx={{
          color: activeCall?.isVideoMuted ? '#f44336' : '#fff',
          bgcolor: activeCall?.isVideoMuted ? 'rgba(244,67,54,0.2)' : 'rgba(255,255,255,0.1)',
          width: btnSize, height: btnSize,
          '&:hover': { bgcolor: activeCall?.isVideoMuted ? 'rgba(244,67,54,0.3)' : 'rgba(255,255,255,0.2)' },
        }}>
          {activeCall?.isVideoMuted ? <VideocamOffIcon sx={{ fontSize: iconSize }} /> : <VideocamIcon sx={{ fontSize: iconSize }} />}
        </IconButton>
      </Tooltip>
      {/* User Details (Vedic Tools) — shows caller name */}
      {onUserDetailsClick && !compact && (
        <Tooltip title={participantName ? `${participantName} — Vedic Tools` : 'User Details'}>
          <IconButton onClick={onUserDetailsClick} sx={{
            color: '#fff', bgcolor: 'rgba(156,39,176,0.3)',
            height: btnSize, borderRadius: 2, px: 1.5, gap: 0.5,
            '&:hover': { bgcolor: 'rgba(156,39,176,0.5)' },
          }}>
            <AutoAwesomeIcon sx={{ fontSize: iconSize }} />
            {participantName && (
              <Typography variant="caption" sx={{ color: '#fff', fontSize: '0.7rem', maxWidth: 80, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {participantName}
              </Typography>
            )}
          </IconButton>
        </Tooltip>
      )}
      {/* Chat */}
      {onChatClick && (
        <Tooltip title="Chat">
          <IconButton onClick={onChatClick} sx={{
            color: '#fff', bgcolor: 'rgba(255,255,255,0.1)',
            width: btnSize, height: btnSize,
            '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' },
          }}>
            <ChatIcon sx={{ fontSize: iconSize }} />
          </IconButton>
        </Tooltip>
      )}
      {/* End Call */}
      <Tooltip title="End Call">
        <IconButton onClick={onEnd} sx={{
          color: '#fff', bgcolor: '#f44336',
          width: endBtnSize, height: endBtnSize, borderRadius: '50%',
          '&:hover': { bgcolor: '#d32f2f' },
          boxShadow: '0 2px 12px rgba(244,67,54,0.4)',
        }}>
          <CallEndIcon sx={{ fontSize: iconSize + 2 }} />
        </IconButton>
      </Tooltip>
    </Box>
  );
};

// ─── GlobalCallPiP ──────────────────────────────────────────────
// Rendered at App.js level — persists across all page navigation.
// User stays on dashboard, astrologer stays on dashboard.
const GlobalCallPiP = () => {
  const navigate = useNavigate();
  const { activeCall, endCall, setExpanded, hasActiveCall, updateBilling } = useCall();
  const { socket } = useChat();
  const { mode, pipSize, pipConfig, isMobile } = useCallLayout();
  const [showToolbar, setShowToolbar] = useState(true);
  const [size, setSize] = useState({ width: pipSize.width, height: pipSize.height });
  const swipeStartY = useRef(null);

  // Sync size with responsive pipSize when mode/device changes
  useEffect(() => {
    if (mode === 'pip') {
      setSize({ width: pipSize.width, height: pipSize.height });
    }
  }, [mode, pipSize]);

  // Subscribe to earnings_updated socket events (astrologer side —
  // backend emits tick_amount per billing tick to the astrologer).
  // Accumulates into activeCall.astrologerEarning for display in CallInfoBar.
  // Uses a ref to track cumulative earning (avoids stale closure on activeCall).
  const earningAccRef = useRef(0);
  useEffect(() => { earningAccRef.current = 0; }, [activeCall?.consultationId]);
  useEffect(() => {
    if (!socket || !hasActiveCall) return;
    const consultationId = activeCall?.consultationId;
    const handleEarningsUpdated = (data) => {
      if (data.consultation_id === consultationId || data.consultationId === consultationId) {
        const tickAmount = data.tick_amount || 0;
        earningAccRef.current += tickAmount;
        updateBilling({ astrologerEarning: earningAccRef.current });
      }
    };
    socket.on('earnings_updated', handleEarningsUpdated);
    return () => { socket.off('earnings_updated', handleEarningsUpdated); };
  }, [socket, hasActiveCall, activeCall?.consultationId, updateBilling]);

  // Don't render if no active call
  if (!hasActiveCall) return null;
  if (activeCall.callType === 'chat') return null; // Chat-only sessions don't need PiP

  const handleEnd = async () => {
    try {
      await api.post(`/consultations/${activeCall.consultationId}/end`, { grace_period_count: 0 });
    } catch (e) { /* best effort */ }
    endCall();
  };

  const handleChatClick = () => {
    const cid = activeCall.consultationId;
    const chatRoomId = activeCall.chatRoomId;
    navigate(`/consultation/${cid}/chat`, {
      state: { chatRoomId, consultationId: cid, type: activeCall.callType, isInstant: true },
    });
  };

  const handleUserDetailsClick = async () => {
    try {
      const res = await api.get(`/consultations/${activeCall.consultationId}/user-profile`);
      const profile = res.data?.data;
      if (!profile) return;

      // Parse Go time.Time ISO strings → simple formats for FreeKundli
      // date_of_birth: "1992-01-15T00:00:00Z" → "1992-01-15"
      // time_of_birth: "0001-01-01T05:30:00Z" → "05:30"
      const dob = profile.date_of_birth ? profile.date_of_birth.split('T')[0] : '';
      const tobISO = profile.time_of_birth || '';
      let tob = '';
      if (tobISO.includes('T')) {
        const timePart = tobISO.split('T')[1] || '';
        tob = timePart.substring(0, 5); // "05:30"
      }

      navigate('/free-kundli', {
        state: {
          autoPopulate: {
            name: profile.name || '',
            date_of_birth: dob,
            time_of_birth: tob,
            place_of_birth: profile.place_of_birth || '',
            latitude: profile.latitude || null,
            longitude: profile.longitude || null,
            gender: profile.gender || 'male',
          },
        },
      });
    } catch (err) {
      console.error('Failed to fetch user profile:', err);
    }
  };

  // Keyboard: Esc to minimize
  const handleKeyDown = (e) => {
    if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
    if (e.key === 'Escape' && mode !== 'pip') setExpanded(false);
  };

  const jitsiProps = {
    consultationId: activeCall.consultationId,
    chatRoomId: activeCall.chatRoomId,
    displayName: activeCall.displayName || 'User',
    videoMuted: activeCall.callType === 'call',
    callType: activeCall.callType,
    onEnd: handleEnd,
  };

  // Single render — Rnd goes fullscreen when expanded, PiP when not.
  // This keeps one JitsiCall instance alive (no remount = no session drop).
  const isFullscreen = mode === 'fullscreen';
  const minW = isMobile ? 120 : 200;
  const minH = isMobile ? 90 : 150;
  const maxW = Math.min(800, window.innerWidth - 20);
  const maxH = Math.min(600, window.innerHeight - 20);

  return (
    <>
      {/* Fullscreen backdrop (swipe-to-minimize on mobile) */}
      {isFullscreen && (
        <Box
          onTouchStart={(e) => { swipeStartY.current = e.touches[0].clientY; }}
          onTouchEnd={(e) => {
            if (swipeStartY.current !== null) {
              const dy = e.changedTouches[0].clientY - swipeStartY.current;
              if (dy > 100) setExpanded(false);
              swipeStartY.current = null;
            }
          }}
          sx={{
            position: 'fixed', inset: 0, zIndex: pipConfig.zIndex + 99,
            bgcolor: 'rgba(0,0,0,0.8)',
          }}
        />
      )}

      <Rnd
        default={{
          x: window.innerWidth - pipSize.width - pipConfig.snapMargin,
          y: window.innerHeight - pipSize.height - pipConfig.snapMargin,
          width: pipSize.width,
          height: pipSize.height,
        }}
        size={isFullscreen
          ? { width: window.innerWidth, height: window.innerHeight }
          : size
        }
        position={isFullscreen ? { x: 0, y: 0 } : undefined}
        minWidth={isFullscreen ? undefined : minW}
        minHeight={isFullscreen ? undefined : minH}
        maxWidth={isFullscreen ? undefined : maxW}
        maxHeight={isFullscreen ? undefined : maxH}
        bounds={isFullscreen ? undefined : 'window'}
        enableResizing={!isFullscreen}
        disableDragging={isFullscreen}
        lockAspectRatio={isFullscreen ? false : 4 / 3}
        onResizeStop={(e, direction, ref) => {
          if (!isFullscreen) setSize({ width: ref.offsetWidth, height: ref.offsetHeight });
        }}
        style={{
          zIndex: pipConfig.zIndex + (isFullscreen ? 100 : 0),
          borderRadius: isFullscreen ? 0 : 12,
          overflow: 'hidden',
          boxShadow: isFullscreen ? 'none' : '0 8px 32px rgba(0,0,0,0.5)',
          border: isFullscreen ? 'none' : '2px solid rgba(255,255,255,0.15)',
          transition: 'width 0.3s ease, height 0.3s ease, border-radius 0.3s ease',
        }}
        cancel=".call-toolbar-btn"
      >
        <Box
          onMouseEnter={() => setShowToolbar(true)}
          onMouseLeave={() => !isMobile && !isFullscreen && setShowToolbar(false)}
          sx={{
            width: '100%', height: '100%', bgcolor: '#000',
            position: 'relative', overflow: 'hidden',
            display: 'flex', flexDirection: 'column',
          }}
        >
          {/* Video — flex: 1 fills available space */}
          <Box sx={{ flex: 1, minHeight: 0, position: 'relative' }}>
            <JitsiCall {...jitsiProps} />
            <CallInfoBar />

            {/* Expand / Minimize button */}
            <Tooltip title={isFullscreen ? 'Minimize' : 'Expand'}>
              <IconButton
                className="call-toolbar-btn"
                onClick={() => setExpanded(!isFullscreen)}
                size="small"
                sx={{
                  position: 'absolute', top: isFullscreen ? 12 : 4, right: isFullscreen ? 12 : 4, zIndex: 12,
                  color: '#fff', bgcolor: 'rgba(0,0,0,0.5)', p: isFullscreen ? 1 : 0.5,
                  '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' },
                }}
              >
                {isFullscreen
                  ? <FullscreenExitIcon sx={{ fontSize: isFullscreen ? 24 : 18 }} />
                  : <FullscreenIcon sx={{ fontSize: 18 }} />
                }
              </IconButton>
            </Tooltip>
          </Box>

          {/* Toolbar — always visible in fullscreen, hover in PiP */}
          <Box
            className="call-toolbar-btn"
            sx={{
              opacity: (showToolbar || isFullscreen) ? 1 : 0,
              transform: (showToolbar || isFullscreen) ? 'translateY(0)' : 'translateY(100%)',
              transition: 'opacity 0.2s, transform 0.2s',
              pointerEvents: 'auto',
            }}
          >
            <CallToolbar
              compact={!isFullscreen && size.width < 280}
              onEnd={handleEnd}
              onChatClick={handleChatClick}
              onUserDetailsClick={handleUserDetailsClick}
              participantName={activeCall?.participantName}
            />
          </Box>
        </Box>
      </Rnd>
    </>
  );
};

export default GlobalCallPiP;
